/**
 * openHousePresenter — pure view-model logic for the open-house surfaces.
 *
 * The backend (`OpenHouses::EventPresenter`) already ships the derived booleans
 * (`registrable`, `checkinable`, `terminal`), the status and the attendance
 * `metrics`. This module maps those into canonical LaisStatusBadge tones, the
 * allowed lifecycle command matrix per status, and per-registration next
 * actions — with the check-in `access_token` redacted unless the caller is
 * explicitly allowed to reveal secrets. No I/O, no clock reads: deterministic.
 */

export const EVENT_STATUSES = Object.freeze([
  'draft',
  'scheduled',
  'open',
  'closed',
  'canceled',
]);

export const REGISTRATION_STATUSES = Object.freeze([
  'registered',
  'confirmed',
  'waitlisted',
  'promoted',
  'checked_in',
  'no_show',
  'canceled',
]);

const EVENT_TONES = Object.freeze({
  draft: 'neutral',
  scheduled: 'info',
  open: 'positive',
  closed: 'neutral',
  canceled: 'danger',
});

const REGISTRATION_TONES = Object.freeze({
  registered: 'info',
  confirmed: 'positive',
  waitlisted: 'warning',
  promoted: 'positive',
  checked_in: 'positive',
  no_show: 'danger',
  canceled: 'neutral',
});

const CONVERSION_LABELS = Object.freeze({
  property_interest: 'interesse',
  demand: 'demanda',
  task: 'tarefa',
  private_visit: 'visita privada',
  offer: 'proposta',
});

// Lifecycle commands allowed per event status. Kept 1:1 with the state machine
// enforced in OpenHouse::LifecycleService so the UI never offers a transition
// the backend will reject.
const EVENT_ACTIONS = Object.freeze({
  draft: ['schedule', 'cancel'],
  scheduled: ['open', 'cancel'],
  open: ['close', 'cancel'],
  closed: [],
  canceled: [],
});

const OCCUPYING_STATUSES = Object.freeze([
  'confirmed',
  'promoted',
  'checked_in',
]);
const CANCELABLE_STATUSES = Object.freeze([
  'registered',
  'confirmed',
  'waitlisted',
  'promoted',
]);

const normalize = value => String(value ?? '').toLowerCase();

export const eventStatusTone = status =>
  EVENT_TONES[normalize(status)] || 'neutral';

export const registrationStatusTone = status =>
  REGISTRATION_TONES[normalize(status)] || 'neutral';

export const eventLifecycleActions = event =>
  EVENT_ACTIONS[normalize(event?.status)] || [];

export const isEventRegistrable = event =>
  event?.registrable === true ||
  ['draft', 'scheduled', 'open'].includes(normalize(event?.status));

export const isEventCheckinable = event =>
  event?.checkinable === true ||
  ['scheduled', 'open'].includes(normalize(event?.status));

const toNumber = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Capacity/attendance read-out. `unlimited` events never render an "esgotado"
 * state; a bounded event derives `available` from capacity minus occupied seats.
 */
export const capacityView = event => {
  const metrics = event?.metrics || {};
  const capacity = event?.capacity ?? metrics.capacity ?? null;
  const unlimited = event?.unlimited === true || capacity === null;
  const occupied = toNumber(
    metrics.occupying ??
      metrics.seats_taken ??
      metrics.confirmed ??
      metrics.seated
  );
  const waitlist = toNumber(metrics.waitlisted);
  const checkedIn = toNumber(metrics.checked_in ?? metrics.present);
  const registered = toNumber(metrics.registered ?? metrics.total);
  const available = unlimited
    ? null
    : Math.max(toNumber(capacity) - occupied, 0);
  return {
    capacity: unlimited ? null : toNumber(capacity),
    unlimited,
    occupied,
    waitlist,
    checkedIn,
    registered,
    available,
    full: unlimited ? false : available === 0,
  };
};

/**
 * Next actions available for a single registration, gated by both the
 * registration status and whether the event can still take check-ins.
 * Never returns an action the backend would refuse.
 */
export const registrationActions = (registration, event) => {
  const status = normalize(registration?.status);
  const actions = [];
  if (status === 'registered' || status === 'waitlisted') {
    actions.push('confirm');
  }
  if (
    isEventCheckinable(event) &&
    OCCUPYING_STATUSES.includes(status) &&
    status !== 'checked_in'
  ) {
    actions.push('check_in');
  }
  if (OCCUPYING_STATUSES.includes(status)) {
    actions.push('convert');
  }
  if (['confirmed', 'promoted'].includes(status)) {
    actions.push('no_show');
  }
  if (CANCELABLE_STATUSES.includes(status)) {
    actions.push('cancel');
  }
  return actions;
};

export const presentContact = (contact = {}, fallbackId = null) => ({
  id: contact?.id ?? fallbackId,
  displayName:
    contact?.display_name || contact?.name || `Contato #${fallbackId || '-'}`,
  email: contact?.email || '',
  phoneNumber: contact?.phone_number || contact?.phone || '',
});

export const presentConversion = (conversion = {}) => {
  const into = normalize(conversion.into);
  return {
    into,
    label: CONVERSION_LABELS[into] || into || 'conversao',
    recordedAt: conversion.recorded_at || null,
    replayed: conversion.replayed === true,
    payload: conversion,
  };
};

export const presentCheckin = (checkin = {}) => ({
  id: checkin.id,
  registrationId: checkin.open_house_registration_id,
  kind: checkin.kind || 'scan',
  checkedInAt: checkin.checked_in_at || null,
  actor: checkin.actor || null,
});

export const presentActivityEvent = (activity = {}) => ({
  id: activity.id,
  name: activity.name || '',
  source: activity.source || '',
  occurredAt: activity.occurred_at || null,
  properties: activity.properties || {},
});

/**
 * Presents a registration row. The opaque `access_token` (the physical check-in
 * pass) is only surfaced when `canRevealSecret` is true; otherwise it is masked
 * so the roster never leaks a live pass to an unauthorized viewer.
 */
export const presentRegistration = (
  registration = {},
  event = {},
  options = {}
) => {
  const canReveal = options.canRevealSecret === true;
  const status = normalize(registration.status);
  const conversions = Array.isArray(registration.conversions)
    ? registration.conversions
    : [];
  const partySize =
    toNumber(registration.party_size) ||
    toNumber(registration.companions) + 1 ||
    1;
  return {
    id: registration.id,
    contactId: registration.contact_id,
    contact: presentContact(registration.contact, registration.contact_id),
    status,
    statusTone: registrationStatusTone(status),
    companions: toNumber(registration.companions),
    partySize,
    origin: registration.origin || 'agent',
    occupying: OCCUPYING_STATUSES.includes(status),
    accessToken: canReveal ? registration.access_token || '' : '',
    accessTokenRedacted: canReveal
      ? false
      : registration.access_token_redacted === true ||
        Boolean(registration.access_token),
    registeredAt: registration.registered_at || null,
    confirmedAt: registration.confirmed_at || null,
    checkedInAt: registration.checked_in_at || null,
    canceledAt: registration.canceled_at || null,
    checkin: registration.checkin || null,
    conversions: conversions.map(conversion => presentConversion(conversion)),
    converted: conversions.length > 0,
    actions: registrationActions(registration, event),
  };
};

export const presentEvent = (event = {}, options = {}) => {
  const status = normalize(event.status);
  return {
    id: event.id,
    title: event.title || '',
    status,
    statusTone: eventStatusTone(status),
    startsAt: event.starts_at || null,
    endsAt: event.ends_at || null,
    timezone: event.timezone || 'UTC',
    imovelId: event.imovel_id ?? event.imovel_listing_id ?? null,
    imovelListingId: event.imovel_listing_id ?? null,
    hosts: Array.isArray(event.hosts) ? event.hosts : [],
    capacity: capacityView(event),
    lifecycleActions: eventLifecycleActions(event),
    registrable: isEventRegistrable(event),
    checkinable: isEventCheckinable(event),
    terminal:
      event.terminal === true || ['closed', 'canceled'].includes(status),
    registrations: (event.registrations || []).map(reg =>
      presentRegistration(reg, event, options)
    ),
    checkins: (event.checkins || []).map(checkin => presentCheckin(checkin)),
    activityEvents: (event.activity_events || []).map(activity =>
      presentActivityEvent(activity)
    ),
  };
};

export default {
  EVENT_STATUSES,
  REGISTRATION_STATUSES,
  eventStatusTone,
  registrationStatusTone,
  eventLifecycleActions,
  isEventRegistrable,
  isEventCheckinable,
  capacityView,
  registrationActions,
  presentRegistration,
  presentContact,
  presentConversion,
  presentCheckin,
  presentActivityEvent,
  presentEvent,
};
